/**
 * SemaforoPanel — Risk traffic-light per centre + notifications + history.
 * Verde  = N+ OPS & planinc ≥ X% (thresholds from DB, configurable via Admin)
 * Amarillo = 1-2 OPS, or planinc M-X%
 * Rojo   = 0 OPS, or planinc < M%
 * Sin datos = no activity at all
 */
import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Search, Bell, Camera, History, TrendingDown, TrendingUp, Minus, RefreshCw, BarChart2, ExternalLink, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import { fetchSemaforo } from '@/services/api';
import { useUserStore } from '@/store/userStore';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DOT_COLOR = {
  verde:     '#22C55E',
  amarillo:  '#F59E0B',
  rojo:      '#EF4444',
  sin_datos: '#6B7280',
};
const LABEL = { verde: 'Verde', amarillo: 'Amarillo', rojo: 'Rojo', sin_datos: 'Sin datos' };

function SemaforoDot({ status, size = 10 }) {
  return (
    <span style={{
      display: 'inline-block', width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: DOT_COLOR[status] || '#6B7280',
      boxShadow: `0 0 ${size / 2}px ${DOT_COLOR[status] || '#6B7280'}70`,
    }} title={LABEL[status]} />
  );
}

function SummaryBar({ summary, activeFilter, onFilter }) {
  const total = Object.values(summary).reduce((a, b) => a + b, 0);
  if (!total) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {['rojo', 'amarillo', 'verde', 'sin_datos'].map(k => {
        const n = summary[k] || 0;
        if (!n) return null;
        const active = activeFilter === k;
        return (
          <button key={k} onClick={() => onFilter(active ? null : k)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all"
            style={{
              background: active ? DOT_COLOR[k] + '25' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${active ? DOT_COLOR[k] : 'rgba(255,255,255,0.08)'}`,
              color: active ? DOT_COLOR[k] : 'var(--vz-text-secondary)',
            }}
            data-testid={`semaforo-filter-${k}`}>
            <SemaforoDot status={k} size={7} /> {LABEL[k]}: {n}
          </button>
        );
      })}
      <span className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>{total} centros</span>
    </div>
  );
}

// ── Notify dialog ─────────────────────────────────────────────────────────────
function NotifyDialog({ open, onClose, filters, authHeader }) {
  const [dryRun, setDryRun] = useState(true);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/kpis/semaforo/notify-rojo`,
        { desde: filters.desde, hasta: filters.hasta, dry_run: dryRun },
        { headers: authHeader() });
      setResult(res.data);
      if (!dryRun && res.data.sent) toast.success(`Email enviado a ${res.data.recipients?.length} destinatarios`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    finally { setLoading(false); }
  };

  const close = () => { setResult(null); setDryRun(true); onClose(); };

  return (
    <Dialog open={open} onOpenChange={v => !v && close()}>
      <DialogContent className="max-w-md" data-testid="notify-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2">
            <Bell size={14} style={{ color: '#EF4444' }} /> Notificar centros en ROJO
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-xs">
          <p style={{ color: 'var(--vz-text-secondary)' }}>
            Enviará un email a <strong>seguridadysalud@valorizasm.com</strong> + todos los gerentes con email configurado en Objetivos,
            con la lista de centros actualmente en estado Rojo.
          </p>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} data-testid="notify-dry-run" />
            <span style={{ color: 'var(--vz-text-secondary)' }}>
              <strong>Simulación</strong> (no envía el email real, solo muestra a quién iría)
            </span>
          </label>
          {result && (
            <div className="rounded-lg p-3 space-y-1.5"
              style={{ background: result.sent || result.dry_run ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                       border: `1px solid ${result.sent || result.dry_run ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
              {result.dry_run ? (
                <>
                  <p style={{ color: '#22C55E' }}>✓ Simulación completada</p>
                  <p>Se enviaría a: <strong>{(result.would_send_to || []).join(', ')}</strong></p>
                  <p>Centros en rojo: <strong style={{ color: '#EF4444' }}>{result.rojos}</strong></p>
                </>
              ) : result.sent ? (
                <>
                  <p style={{ color: '#22C55E' }}>✓ Email enviado correctamente</p>
                  <p>Destinatarios: {result.recipients?.join(', ')}</p>
                  <p>Centros en rojo: <strong style={{ color: '#EF4444' }}>{result.rojos}</strong></p>
                </>
              ) : (
                <p style={{ color: '#F59E0B' }}>ℹ {result.reason || 'Sin centros en rojo'}</p>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={close}>Cerrar</Button>
            <Button size="sm" onClick={run} disabled={loading}
              style={{ background: dryRun ? '#4A9EFF20' : '#EF444420', border: `1px solid ${dryRun ? '#4A9EFF' : '#EF4444'}`, color: dryRun ? '#4A9EFF' : '#EF4444' }}
              data-testid="notify-send-btn">
              {loading ? <RefreshCw size={11} className="animate-spin mr-1" /> : null}
              {dryRun ? 'Simular' : 'Enviar email'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

import { fetchDiasSinIncidentes } from '@/services/api';

const RACHA_THRESHOLDS = { rojo: 7, amarillo: 30 };
function _rachaColor(dias) {
  if (dias == null) return '#22C55E';
  if (dias < RACHA_THRESHOLDS.rojo) return '#EF4444';
  if (dias < RACHA_THRESHOLDS.amarillo) return '#F59E0B';
  return '#22C55E';
}

// ── Centro Detail Modal (weekly trend chart) ─────────────────────────────────
function CentroDetailModal({ centro, onClose, authHeader }) {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['tendencia-semanal', centro?.sede_codigo],
    queryFn: () => axios.get(
      `${API}/api/kpis/tendencia-semanal?sede_codigo=${encodeURIComponent(centro.sede_codigo)}&semanas=8`,
      { headers: authHeader() }
    ).then(r => r.data),
    enabled: !!centro?.sede_codigo,
    staleTime: 5 * 60 * 1000,
  });
  const { data: rachaData } = useQuery({
    queryKey: ['dias-sin-incidentes'],
    queryFn: fetchDiasSinIncidentes,
    staleTime: 5 * 60 * 1000,
    enabled: !!centro?.sede_codigo,
  });
  const racha = rachaData?.items?.find(i => i.codigo === centro?.sede_codigo);

  if (!centro) return null;
  const semColor = DOT_COLOR[centro.semaforo] || '#6B7280';
  const semanas = data?.semanas || [];

  return (
    <Dialog open={!!centro} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <SemaforoDot status={centro.semaforo} size={12} />
            <div>
              <p className="text-sm font-bold" style={{ color: 'var(--vz-text-primary)' }}>{centro.nombre || centro.sede_codigo}</p>
              <p className="text-[11px] font-normal mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
                {centro.delegacion} · {centro.sede_codigo}
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Current period KPIs */}
        <div className="grid grid-cols-4 gap-3 mt-1">
          {[
            { label: 'OPS', value: centro.ops_count, color: '#4A9EFF' },
            { label: 'Liderazgo', value: centro.lid_count, color: '#22C55E' },
            { label: 'Near Miss', value: centro.nm_count, color: '#F97316' },
            { label: 'Días sin OPS', value: centro.dias_sin_ops != null ? `${centro.dias_sin_ops}d` : '—',
              color: centro.inactivo_30d ? '#EF4444' : centro.dias_sin_ops > 14 ? '#F59E0B' : '#22C55E' },
          ].map(({ label, value, color }) => (
            <div key={label} className="rounded-lg p-2.5 text-center" style={{ background: 'var(--vz-bg-card)', border: '1px solid var(--vz-border-subtle)' }}>
              <p className="text-lg font-bold font-mono" style={{ color }}>{value}</p>
              <p className="text-[10px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>{label}</p>
            </div>
          ))}
        </div>

        {/* Inactivity warning */}
        {centro.inactivo_30d && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg text-xs"
            style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#EF4444' }}>
            <Clock size={13} />
            <span>⚠ <strong>{centro.dias_sin_ops} días</strong> sin ningún registro OPS — semáforo forzado a rojo (umbral: 30 días)</span>
          </div>
        )}

        {/* Racha días sin incidentes (31/08 follow-up) */}
        {racha && (
          <div className="grid grid-cols-2 gap-3" data-testid="centro-detail-racha">
            {[
              { label: 'Sin Accidentes', dias: racha.dias_sin_accidente },
              { label: 'Sin Casi Accidentes', dias: racha.dias_sin_nm },
            ].map(({ label, dias }) => {
              const color = _rachaColor(dias);
              return (
                <div key={label} className="rounded-lg p-2.5 flex items-center justify-between"
                  style={{ background: `${color}10`, border: `1px solid ${color}35` }}>
                  <span className="text-[11px] font-medium" style={{ color: 'var(--vz-text-secondary)' }}>{label}</span>
                  <span className="text-sm font-bold font-mono" style={{ color }}>
                    {dias == null ? 'sin registro' : `${dias}d`}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Weekly trend chart */}
        <div>
          <p className="text-[10px] uppercase tracking-wider mb-3 flex items-center gap-1.5" style={{ color: 'var(--vz-text-muted)' }}>
            <BarChart2 size={11} style={{ color: '#4A9EFF' }} /> Comparativa últimas 8 semanas
          </p>
          {isLoading ? (
            <Skeleton className="h-44 w-full" />
          ) : semanas.length > 0 ? (
            <ResponsiveContainer width="100%" height={176}>
              <BarChart data={semanas} margin={{ top: 4, right: 4, left: -20, bottom: 0 }} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="semana" tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<HTooltip />} />
                <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="ops" name="OPS" fill="#4A9EFF" radius={[3, 3, 0, 0]} maxBarSize={28} />
                <Bar dataKey="liderazgo" name="Liderazgo" fill="#22C55E" radius={[3, 3, 0, 0]} maxBarSize={28} />
                <Bar dataKey="nm" name="Near Miss" fill="#F97316" radius={[3, 3, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-xs text-center py-8" style={{ color: 'var(--vz-text-muted)' }}>Sin datos históricos disponibles</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-1 border-t" style={{ borderColor: 'var(--vz-border-subtle)' }}>
          <Button variant="ghost" size="sm" className="text-xs" onClick={onClose}>Cerrar</Button>
          <Button size="sm" className="text-xs gap-1.5"
            style={{ background: `${semColor}22`, border: `1px solid ${semColor}55`, color: semColor }}
            onClick={() => { onClose(); navigate(`/ops?sede=${encodeURIComponent(centro.sede_codigo)}`); }}
            data-testid="centro-detail-goto-ops">
            <ExternalLink size={11} /> Ver en OPS
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── History chart ─────────────────────────────────────────────────────────────
const HTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded px-2.5 py-1.5 text-xs shadow-xl" style={{ background: '#1E2435', border: '1px solid rgba(255,255,255,0.1)' }}>
      <p className="font-semibold text-white mb-1">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-1.5 py-0.5">
          <span className="w-2 h-2 rounded-full" style={{ background: p.fill }} />
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>{p.name}:</span>
          <span className="font-bold text-white">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

function HistoryPanel({ authHeader }) {
  const { data, isLoading } = useQuery({
    queryKey: ['semaforo-history'],
    queryFn: () => axios.get(`${API}/api/kpis/semaforo/history?semanas=10`, { headers: authHeader() }).then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });
  const qc = useQueryClient();
  const [snapshotting, setSnapshotting] = useState(false);

  const takeSnapshot = async () => {
    setSnapshotting(true);
    try {
      await axios.post(`${API}/api/kpis/semaforo/snapshot`, {}, { headers: authHeader() });
      toast.success('Snapshot guardado');
      qc.invalidateQueries({ queryKey: ['semaforo-history'] });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al guardar snapshot'); }
    finally { setSnapshotting(false); }
  };

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  const snaps = data?.snapshots || [];

  return (
    <div className="space-y-3" data-testid="semaforo-history-panel">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold text-white flex items-center gap-1.5">
          <History size={12} /> Histórico semanal
        </p>
        <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1.5"
          onClick={takeSnapshot} disabled={snapshotting} data-testid="semaforo-snapshot-btn">
          <Camera size={10} /> {snapshotting ? 'Guardando...' : 'Guardar snapshot ahora'}
        </Button>
      </div>

      {snaps.length === 0 ? (
        <div className="text-[11px] py-6 text-center" style={{ color: 'var(--vz-text-muted)' }}>
          Sin snapshots. Pulsa "Guardar snapshot ahora" para iniciar el histórico,
          o espera al cron automático del lunes.
        </div>
      ) : (
        <>
          <div className="rounded-xl p-4" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={snaps.map(s => ({
                semana: s.semana?.replace(/^\d{4}-/, '') || s.fecha_snapshot?.slice(5, 10),
                Verde: s.summary?.verde || 0,
                Amarillo: s.summary?.amarillo || 0,
                Rojo: s.summary?.rojo || 0,
                'Sin datos': s.summary?.sin_datos || 0,
              }))} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
                <XAxis dataKey="semana" tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.4)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.4)' }} axisLine={false} tickLine={false} width={24} />
                <Tooltip content={<HTooltip />} />
                <Legend wrapperStyle={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', paddingTop: 6 }} iconType="circle" iconSize={7} />
                <Bar dataKey="Rojo" stackId="a" fill="#EF4444" radius={[0,0,0,0]} />
                <Bar dataKey="Amarillo" stackId="a" fill="#F59E0B" />
                <Bar dataKey="Verde" stackId="a" fill="#22C55E" />
                <Bar dataKey="Sin datos" stackId="a" fill="#6B7280" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Trend table (last 2 snapshots) */}
          {snaps.length >= 2 && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--vz-text-muted)' }}>
                Tendencia vs semana anterior ({snaps[snaps.length - 2]?.semana} → {snaps[snaps.length - 1]?.semana})
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {['rojo', 'amarillo', 'verde', 'sin_datos'].map(k => {
                  const curr = snaps[snaps.length - 1]?.summary?.[k] || 0;
                  const prev = snaps[snaps.length - 2]?.summary?.[k] || 0;
                  const diff = curr - prev;
                  const Icon = diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
                  return (
                    <div key={k} className="rounded-lg p-2.5 text-center"
                      style={{ background: DOT_COLOR[k] + '0A', border: `1px solid ${DOT_COLOR[k]}25` }}>
                      <div className="text-sm font-bold" style={{ color: DOT_COLOR[k] }}>{curr}</div>
                      <div className="text-[9px] uppercase" style={{ color: 'var(--vz-text-muted)' }}>{LABEL[k]}</div>
                      <div className="flex items-center justify-center gap-0.5 mt-1 text-[10px]"
                        style={{ color: diff === 0 ? 'var(--vz-text-muted)' : diff > 0 ? (k === 'rojo' ? '#EF4444' : '#22C55E') : (k === 'rojo' ? '#22C55E' : '#EF4444') }}>
                        <Icon size={9} /> {diff > 0 ? '+' : ''}{diff}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Main SemaforoPanel ────────────────────────────────────────────────────────
export function SemaforoPanel({ filters = {} }) {
  const { token, user } = useUserStore();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [showNotify, setShowNotify] = useState(false);
  const [selectedCentro, setSelectedCentro] = useState(null);

  const isAdmin = ['admin', 'superadmin'].includes(user?.role);
  const authHeader = () => ({ Authorization: `Bearer ${token}` });

  const { data, isLoading } = useQuery({
    queryKey: ['semaforo', filters.desde, filters.hasta, filters.delegacion],
    queryFn: () => fetchSemaforo({ desde: filters.desde, hasta: filters.hasta, delegacion: filters.delegacion }),
    staleTime: 5 * 60 * 1000,
    enabled: !!token,
  });

  if (isLoading) return (
    <div data-testid="semaforo-panel-loading">
      <Skeleton className="h-8 w-80 mb-3" /><Skeleton className="h-48 w-full" />
    </div>
  );

  if (!data?.centros?.length) return null;

  const centros = data.centros.filter(c => {
    if (activeFilter && c.semaforo !== activeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (c.nombre || '').toLowerCase().includes(q) || (c.sede_codigo || '').includes(q);
    }
    return true;
  });

  const thresholds = data.thresholds || {};

  return (
    <div className="space-y-4" data-testid="semaforo-panel">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            🚦 Semáforo de riesgo por centro
          </h3>
          <p className="text-[10px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
            ≥{thresholds.ops_verde || 3} OPS + checklist ≥{thresholds.planinc_verde || 90}% = verde · Click en un centro para ver detalle · +30d sin OPS → rojo automático
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: 'var(--vz-text-muted)' }} />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar centro..." className="h-7 text-xs pl-7 w-40"
              data-testid="semaforo-search" />
          </div>
          <Button size="sm" variant="outline"
            className="h-7 text-[10px] gap-1.5"
            onClick={() => setShowHistory(v => !v)}
            style={{ borderColor: showHistory ? '#4A9EFF' : undefined, color: showHistory ? '#4A9EFF' : undefined }}
            data-testid="semaforo-history-btn">
            <History size={10} /> Histórico
          </Button>
          {isAdmin && (
            <Button size="sm" variant="outline"
              className="h-7 text-[10px] gap-1.5"
              onClick={() => setShowNotify(true)}
              style={{ borderColor: 'rgba(239,68,68,0.4)', color: '#EF4444' }}
              data-testid="semaforo-notify-btn">
              <Bell size={10} /> Notificar rojos
            </Button>
          )}
        </div>
      </div>

      <SummaryBar summary={data.summary} activeFilter={activeFilter} onFilter={setActiveFilter} />

      {/* History panel (toggle) */}
      {showHistory && (
        <div className="rounded-xl p-4" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}>
          <HistoryPanel authHeader={authHeader} />
        </div>
      )}

      {/* Centro grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
        {centros.map(c => (
          <button key={c.sede_codigo} onClick={() => setSelectedCentro(c)}
            className="flex items-start gap-2.5 p-2.5 rounded-lg text-left transition-all hover:scale-[1.01]"
            style={{ background: DOT_COLOR[c.semaforo] + '08', border: `1px solid ${DOT_COLOR[c.semaforo]}28` }}
            data-testid={`semaforo-centro-${c.sede_codigo}`}>
            <SemaforoDot status={c.semaforo} size={9} style={{ marginTop: 3 }} />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-medium truncate" style={{ color: 'var(--vz-text-primary)' }} title={c.nombre}>
                {c.nombre || c.sede_codigo}
              </div>
              <div className="text-[9px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
                {c.delegacion ? `${c.delegacion} · ` : ''}
                <span style={{ color: c.ops_count > 0 ? '#4A9EFF' : '#EF4444' }}>{c.ops_count} OPS</span>
                {' · '}
                <span style={{ color: c.lid_count > 0 ? '#22C55E' : 'var(--vz-text-muted)' }}>{c.lid_count} Lid</span>
                {' · '}
                <span style={{ color: c.nm_count > 0 ? '#F97316' : 'var(--vz-text-muted)' }}>{c.nm_count} NM</span>
                {c.planinc_pct != null && (
                  <> · <span style={{ color: c.planinc_pct >= (thresholds.planinc_verde||90) ? '#22C55E' : c.planinc_pct >= (thresholds.planinc_amarillo||70) ? '#F59E0B' : '#EF4444' }}>
                    {c.planinc_pct}%
                  </span></>
                )}
                {c.inactivo_30d && (
                  <> · <span style={{ color: '#EF4444' }} title="Sin OPS en más de 30 días">⏱{c.dias_sin_ops}d</span></>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {centros.length === 0 && (
        <div className="text-center py-8 text-xs" style={{ color: 'var(--vz-text-muted)' }}>
          No hay centros que coincidan con el filtro.
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-4 pt-2 border-t" style={{ borderColor: 'var(--vz-border-subtle)' }}>
        {[
          { k: 'verde', desc: `≥${thresholds.ops_verde||3} OPS · checklist ≥${thresholds.planinc_verde||90}%` },
          { k: 'amarillo', desc: `1-${(thresholds.ops_verde||3)-1} OPS · checklist ${thresholds.planinc_amarillo||70}-${(thresholds.planinc_verde||90)-1}%` },
          { k: 'rojo', desc: `0 OPS · checklist <${thresholds.planinc_amarillo||70}% · o +${thresholds.inactivity_days||30}d sin OPS` },
          { k: 'sin_datos', desc: 'Sin actividad registrada' },
        ].map(({ k, desc }) => (
          <div key={k} className="flex items-center gap-1.5 text-[9px]" style={{ color: 'var(--vz-text-muted)' }}>
            <SemaforoDot status={k} size={7} />
            <span style={{ color: DOT_COLOR[k] }}>{LABEL[k]}</span>: {desc}
          </div>
        ))}
      </div>

      {/* Notify dialog */}
      <NotifyDialog open={showNotify} onClose={() => setShowNotify(false)} filters={filters} authHeader={authHeader} />

      {/* Centro detail modal */}
      <CentroDetailModal centro={selectedCentro} onClose={() => setSelectedCentro(null)} authHeader={authHeader} />
    </div>
  );
}
